import { computed } from 'vue';

import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { RouteLocationNamedRaw } from 'vue-router';
import { useAppSwitcher } from '@/composables/useAppSwitcher';
import { useBasicAuth } from '@/composables/useBasicAuth';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { toggleMaven } from '@/plugins/maven';
import { trackEvent, EVENTS } from '@/plugins/gainsight';
import { usePermissions } from '@/composables/usePermissions';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import {
	APP_SWITCHER_SLUGS,
	ROLES,
	FEATURE_FLAGS,
} from '@teamworksdev/influencer/core';
export class PrimaryNavigationItem {
	id?: string;
	title: string;
	appendIcon?: string;
	prependIcon?: string;
	enabled?: boolean;
	visible?: boolean;
	badge?: string;
	to?: RouteLocationNamedRaw;
	link?: string;
	action?(): void;

	constructor(navItem: PrimaryNavigationItem) {
		this.id = navItem.id ?? crypto.randomUUID();
		this.title = navItem.title;
		this.appendIcon = navItem.appendIcon;
		this.prependIcon = navItem.prependIcon;
		this.enabled = navItem.enabled ?? true;
		this.visible = navItem.visible ?? true;
		this.badge = navItem.badge;
		this.to = navItem.to;
		this.link = navItem.link;
		this.action = navItem.action;
	}
}

const withDefaults = (items: PrimaryNavigationItem[]) => {
	return items.map((i) => new PrimaryNavigationItem(i));
};

export function usePrimaryNavigation() {
	const { t } = useI18n();
	const authStore = useAuthStore();
	const userStore = useUserStore();
	const { appSwitcherProfile } = storeToRefs(useUserStore());
	const { switchApp, appsMetaData } = useAppSwitcher();
	const { logOut } = useBasicAuth();
	const { hasContentHubDashboardAccess, hasContentHubAccess, hasContentTasksAccess, hasBulletinsAccess, hasTransactionsAccess } = usePermissions();
	const isContentTasksEnabled = useLDFlag(FEATURE_FLAGS.CONTENT_TASKS, false);
	const isTeamManagementEnabled = useLDFlag(FEATURE_FLAGS.V2_TEAM_MANAGEMENT_MENU_ITEM, false);

	const isMenuItemShown = ({ visible }: PrimaryNavigationItem): boolean => !!visible;

	const mainItems = computed(() => withDefaults([
		{
			title: t('navigationMenu.dashboard'),
			prependIcon: 'home',
			visible: hasContentHubDashboardAccess.value,
			to: { name: 'ContentHubDashboard' },
		},
		{
			title: t('navigationMenu.contentHub'),
			prependIcon: 'image',
			to: { name: 'ContentHubIndex' },
			visible: hasContentHubAccess.value,
		},
		{
			title: t('navigationMenu.collaborationHub'),
			prependIcon: 'handshake',
			to: { name: 'NotFound' },
			enabled: false,
			visible: userStore.profileAccess.hasCollaborationHubRead && !isContentTasksEnabled.value,
		},
		{
			title: t('navigationMenu.contentTasks'),
			prependIcon: 'task',
			to: { name: 'ContentTasks' },
			visible: hasContentTasksAccess.value && isContentTasksEnabled.value,
		},
		{
			title: t('navigationMenu.roster'),
			prependIcon: 'person_search',
			to: { name: 'Roster' },
			enabled: true,
			visible: userStore.profileAccess.hasTeamManagementRead,
		},
		{
			title: t('navigationMenu.teamManagement'),
			prependIcon: 'contacts',
			enabled: true,
			visible: userStore.profileAccess.hasTeamManagementRead && isTeamManagementEnabled.value,
			link: `${import.meta.env.VITE_INFLCR_V2_BASE}/auth/swap?role=${ROLES.TEAM_USER}&token=${authStore.token?.access_token}&profileUuid=${userStore.profile?.uuid}`,
		},
		{
			title: t('navigationMenu.browseAthletes'),
			prependIcon: 'person_search',
			to: { name: 'ExchangeAthletes' },
			visible: userStore.profileAccess.hasExchangeWrite,
		},
		{
			title: t('navigationMenu.bulletins'),
			prependIcon: 'campaign',
			to: { name: 'ExchangeBulletins' },
			visible: hasBulletinsAccess.value,
		},
		{
			title: t('navigationMenu.conversations'),
			prependIcon: 'sms',
			to: { name: 'ExchangeConversations' },
			visible: userStore.profileAccess.hasExchangeWrite,
		},
		{
			title: t('pageComplianceDashboard.metaTitle'),
			prependIcon: 'home',
			visible: userStore.profileAccess.isComplianceOfficer,
			to: { name: 'ComplianceDashboard' },
		},
		{
			title: t('navigationMenu.transactions'),
			prependIcon: 'local_atm',
			to: { name: userStore.transactionsRouteName },
			visible: hasTransactionsAccess.value,
		},
		{
			title: t('navigationMenu.brands'),
			prependIcon: 'handshake',
			to: { name: 'ComplianceBrands' },
			visible: userStore.profile?.role_id === ROLES.COMPLIANCE_OFFICER
				&& userStore.profile.school.local_exchange_enabled,
		},
		{
			title: t('navigationMenu.exchangeSettings'),
			prependIcon: 'settings',
			to: { name: 'ExchangeSettings' },
			visible: userStore.profileAccess.hasExchangeWrite,
		},
		{
			title: t('navigationMenu.inbox'),
			prependIcon: '$twi-conversation',
			enabled: false,
			visible: false,
		},
		{
			title: t('pageUploadTransactions.metaTitle'),
			prependIcon: 'attach_money',
			visible: userStore.profileAccess.hasTransactionsImportWrite,
			to: { name: 'UploadTransactions' },
		},
		{
			title: t('pageNilAssistDashboard.metaTitle'),
			prependIcon: 'insert_chart',
			visible: userStore.profileAccess.isComplianceOfficer,
			to: { name: 'ComplianceDashboards' },
		},
		{
			title: t('pageComplianceAgents.metaTitle'),
			prependIcon: 'group',
			to: { name: 'ComplianceAgents' },
			visible: userStore.profileAccess.isComplianceOfficer,
		},
		{
			title: t('pageComplianceSettings.metaTitle'),
			prependIcon: 'settings',
			to: { name: 'ComplianceSettingsSchool' },
			visible: userStore.profileAccess.isComplianceOfficer,
		},
		{
			title: t('pageReleaseNotes.metaTitle'),
			prependIcon: 'tips_and_updates',
			to: { name: 'ReleaseNotesIndex' },
			visible: true,
		},
	]).filter(isMenuItemShown));

	const appendedItems = computed(() => withDefaults([
		{
			title: t('navigationMenu.search'),
			enabled: false,
			visible: false,
		},
		{
			title: t('navigationMenu.getHelp'),
			action: () => toggleMaven(),
		},
		{
			title: t('navigationMenu.helpCenter'),
			link: 'https://help.teamworks.com/influencer/s/',
		},
		{
			title: t('navigationMenu.learningPortal'),
			link: 'https://learn.inflcr.com/',
		},
	]).filter(isMenuItemShown));

	const userItems = computed(() => withDefaults([
		{
			title: t('navigationMenu.myProfile'),
			to: { name: 'Profile' },
			visible: userStore.profileAccess.hasMyProfile,
		},
		{
			title: t('navigationMenu.accountSettings'),
			to: { name: 'AccountSettingsParent' },
		},
		{
			appendIcon: 'logout',
			title: t('logOut'),
			action: () => {
				logOut();
			},
		},
	]).filter(isMenuItemShown));

	const footerItems = computed(() => {
		const hubItem = appsMetaData.find((app) => app.slug === APP_SWITCHER_SLUGS.HUB);
		if (!hubItem) return [];

		return withDefaults([
			{
				title: t('navigationMenu.userManagement'),
				prependIcon: 'menu_book',
				action: () => {
					trackEvent(EVENTS.RETURN_TO_TUM);
					switchApp(hubItem.slug, { redirect: '/profiles' });
				},
				visible: (userStore.profileAccess.isComplianceOfficer || userStore.profileAccess.isTeamUser)
					&& !!appSwitcherProfile?.value?.[`${APP_SWITCHER_SLUGS.HUB}_target_url`],
			},
		]).filter(isMenuItemShown);
	});

	const appSwitcherItems = computed(() => {
		const allItems = appsMetaData.map((app) => {
			const isThisApp = app.slug === APP_SWITCHER_SLUGS.INFLCR;
			return {
				title: app.title,
				action: isThisApp ? undefined : () => switchApp(app.slug),
				prependIcon: app.icon,
				appendIcon: isThisApp ? 'check' : undefined,
				visible: isThisApp || (!!appSwitcherProfile?.value?.[`${app.slug}_target_url`] && appSwitcherProfile.value.login_access.web[`${app.slug}`]),
			};
		});
		return withDefaults(allItems.filter(isMenuItemShown));
	});

	return {
		mainItems,
		appendedItems,
		footerItems,
		userItems,
		appSwitcherItems,
	};
}
